import { swimEffectiveChop } from "./swim-scoring";

export type SwimChopAssessment = {
  effectiveChopM: number;
  confidence: "full" | "inferred";
  windProxyKmh: number | null;
};

/** Surface-state confidence for personal fit and explanation, never quality. */
export function assessSwimChop(input: {
  swellHeightM: number;
  windWaveHeightM: number | null;
  swellPeriodS: number | null;
  windSpeedKmh: number;
}): SwimChopAssessment {
  const confidence = input.windWaveHeightM === null ? "inferred" : "full";
  return {
    effectiveChopM: swimEffectiveChop(
      input.swellHeightM,
      input.windWaveHeightM,
      input.swellPeriodS,
    ),
    confidence,
    windProxyKmh: confidence === "inferred" ? input.windSpeedKmh : null,
  };
}

export type ExitWindChillResult = {
  exitFeelsLikeC: number;
  waterTempC: number;
  delta: number;
  gearNote: string | null;
};

type ExitWindChillInput = {
  wetBulbC: number;
  windSpeedKmh: number;
  waterTempC: number;
};

export function computeExitWindChill(input: ExitWindChillInput): ExitWindChillResult | null {
  const { wetBulbC, windSpeedKmh, waterTempC } = input;

  const exitTemp = wetBulbC - windSpeedKmh * 0.2;
  const clamped = Math.max(exitTemp, wetBulbC - 15); // sanity clamp
  const delta = waterTempC - clamped;

  if (delta < 3) return null;

  const gearNote = clamped < 10 ? "Bring a changing robe" : null;

  return {
    exitFeelsLikeC: Math.round(clamped),
    waterTempC,
    delta: Math.round(delta),
    gearNote,
  };
}

export type JellyfishLevel = "low" | "low-moderate" | "moderate" | "high" | "peak";

export type JellyfishResult = {
  level: JellyfishLevel;
  label: string;
  disclaimer: string;
};

export function assessJellyfishRisk(sstC: number, month: number): JellyfishResult {
  const disclaimer = "Seasonal indicator - check local reports";
  const isSummer = month >= 7 && month <= 9;

  let level: JellyfishLevel;
  if (sstC < 10 && !isSummer) {
    level = "low";
  } else if (sstC < 14) {
    level = isSummer ? "moderate" : "low-moderate";
  } else if (sstC < 17) {
    level = isSummer ? "high" : "moderate";
  } else if (sstC < 20) {
    level = "high";
  } else {
    level = "peak";
  }

  const labels: Record<JellyfishLevel, string> = {
    low: "Jellyfish: Low risk",
    "low-moderate": "Jellyfish: Low-moderate",
    moderate: "Jellyfish: Moderate",
    high: "Jellyfish: High",
    peak: "Jellyfish: Peak season",
  };

  return { level, label: labels[level], disclaimer };
}

export type SwimDifficulty = {
  score: number;
  label: "Effortless" | "Easy" | "Moderate" | "Challenging" | "Expert only";
  dominantFactor: "current" | "chop" | "wind";
};

type SwimDifficultyInput = {
  currentMs: number;
  chopM: number;
  windKmh: number;
};

export function computeSwimDifficulty(input: SwimDifficultyInput): SwimDifficulty {
  const { currentMs, chopM, windKmh } = input;

  const currentContrib = Math.min((currentMs / 0.4) * 5, 5);
  const chopContrib = Math.min((chopM / 1.0) * 3, 3);
  const windContrib = Math.min((windKmh / 30) * 2, 2);

  const score = Math.min(currentContrib + chopContrib + windContrib, 10);

  let label: SwimDifficulty["label"];
  if (score < 2) label = "Effortless";
  else if (score < 4) label = "Easy";
  else if (score < 6) label = "Moderate";
  else if (score < 8) label = "Challenging";
  else label = "Expert only";

  const factors = { current: currentContrib, chop: chopContrib, wind: windContrib };
  const dominantFactor = (Object.entries(factors).sort((a, b) => b[1] - a[1])[0][0]) as SwimDifficulty["dominantFactor"];

  return { score, label, dominantFactor };
}
